/**
 * Canonical address primitives for Regrid parcel selection and the bounded
 * Bergen provider-spend policy. Keep them pure so callers share house,
 * street-suffix, and ZIP rules while selecting unit semantics explicitly.
 */

const STREET_SUFFIXES: Readonly<Record<string, string>> = {
  AVENUE: "AVE", BOULEVARD: "BLVD", CIRCLE: "CIR", COURT: "CT",
  DRIVE: "DR", LANE: "LN", PARKWAY: "PKWY", PLACE: "PL", ROAD: "RD",
  STREET: "ST", TERRACE: "TER", TRAIL: "TRL",
};

export interface RegridAddressIdentity {
  houseNumber: string;
  streetKey: string;
  zip: string;
}

export interface RegridStreetIdentity {
  houseNumber: string;
  streetKey: string;
}

function normalizeIdentityPart(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[^A-Z0-9]+/gi, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/** Preserve street identity while accepting equivalent suffix abbreviations. */
function normalizeStreetKey(value: string): string {
  const parts = normalizeIdentityPart(value).split(" ").filter(Boolean);
  const last = parts.length - 1;
  if (last > 0) parts[last] = STREET_SUFFIXES[parts[last]] ?? parts[last];
  return parts.join(" ");
}

function parseStreetIdentity(
  address: string | null | undefined,
  ignoreTrailingUnit: boolean,
): RegridStreetIdentity | null {
  if (typeof address !== "string") return null;
  const parsingAddress = ignoreTrailingUnit ? address.normalize("NFKD") : address;
  let street = parsingAddress.trim().split(",")[0]?.trim() ?? "";
  if (ignoreTrailingUnit) {
    // Only the spend guard ignores unit aliases. The general parcel selector
    // must preserve them so an apartment request cannot match a parent parcel.
    street = street
      // A unit hash may touch the street name (`Place#2`) or be left dangling
      // (`Place #`). Treat either spelling conservatively at the spend gate.
      .replace(/\s*#\s*[^,]*$/i, "")
      // Unit numbers are often compact (`Apt2`, `Unit2`). The following
      // non-letter boundary catches those without treating street names such
      // as `Aptitude` as unit aliases.
      .replace(/\s+(?:APT|APARTMENT|UNIT|SUITE|STE)(?=$|[^A-Z])\.?[\s\S]*$/i, "")
      .trim();
  }
  const parsed = street.match(/^(\d+)\s+(.+)$/);
  if (!parsed) return null;
  const streetKey = normalizeStreetKey(parsed[2]);
  if (!streetKey) return null;
  return { houseNumber: parsed[1], streetKey };
}

/** House number and canonical street only, preserving unit identity. */
export function parseRegridStreetIdentity(
  address: string | null | undefined,
): RegridStreetIdentity | null {
  return parseStreetIdentity(address, false);
}

/** House number and canonical street only, ignoring units for the spend guard. */
export function parseRegridSpendGuardStreetIdentity(
  address: string | null | undefined,
): RegridStreetIdentity | null {
  return parseStreetIdentity(address, true);
}

function lastZip(value: string): string | null {
  const matches = Array.from(value.matchAll(/\b(\d{5})(?:-\d{4})?\b/g));
  return matches[matches.length - 1]?.[1] ?? null;
}

/**
 * House number, canonical street key, and ZIP5 for a free-text address.
 * ZIP+4 is intentionally reduced to ZIP5, matching Regrid's parcel matcher.
 * Returns null unless all identity fields are present.
 */
function parseAddressIdentity(
  address: string | null | undefined,
  zipOverride: string | null | undefined,
  streetParser: typeof parseRegridStreetIdentity,
): RegridAddressIdentity | null {
  if (typeof address !== "string") return null;
  const trimmed = address.trim();
  if (!trimmed) return null;

  const zipSource = typeof zipOverride === "string" && zipOverride.trim()
    ? zipOverride
    : trimmed;
  const zip = lastZip(zipSource);
  if (!zip) return null;
  const streetIdentity = streetParser(trimmed);
  if (!streetIdentity) return null;
  return { ...streetIdentity, zip };
}

export function parseRegridAddressIdentity(
  address: string | null | undefined,
  zipOverride?: string | null,
): RegridAddressIdentity | null {
  return parseAddressIdentity(address, zipOverride, parseRegridStreetIdentity);
}

/** Full identity used only for conservative provider-spend recognition. */
export function parseRegridSpendGuardAddressIdentity(
  address: string | null | undefined,
  zipOverride?: string | null,
): RegridAddressIdentity | null {
  return parseAddressIdentity(address, zipOverride, parseRegridSpendGuardStreetIdentity);
}

export function regridAddressIdentitiesAgree(
  a: RegridAddressIdentity,
  b: RegridAddressIdentity,
): boolean {
  return a.houseNumber === b.houseNumber &&
    a.streetKey === b.streetKey &&
    a.zip === b.zip;
}
